<template>
    <div class="meta-insights-audience">
        <div class="header">
            <div class="title-block">
                <div class="title">{{ $t("metaInsights.audienceTitle") }}</div>
                <div class="subtitle">{{ $t("metaInsights.audienceSubtitle") }}</div>
            </div>
            <div class="toolbar">
                <a-select v-model="metric" style="width: 160px;" @change="onMetricChange">
                    <a-select-option value="impressions">
                        {{ $t("metaInsights.kpi.impressions") }}
                    </a-select-option>
                    <a-select-option value="reach">{{ $t("metaInsights.kpi.reach") }}</a-select-option>
                    <a-select-option value="clicks">{{ $t("metaInsights.kpi.clicks") }}</a-select-option>
                    <a-select-option value="spend">{{ $t("metaInsights.kpi.spend") }}</a-select-option>
                </a-select>
                <DateRangePicker v-model="dateRange" @change="onDateRangeChange" />
                <SupsisButton ghost @click="loadAll" :disabled="loading">
                    <i class="material-icons" :class="{ spinning: loading }">refresh</i>
                    {{ $t("metaInsights.refresh") }}
                </SupsisButton>
            </div>
        </div>

        <div class="grid">
            <BreakdownChart
                :title="$t('metaInsights.breakdown.gender')"
                :rows="genderRows"
                breakdown-key="gender"
                :metric="metric"
                chart-type="pie"
                :loading="loadingDemographics"
                :label-map="genderLabels"
                :currency="currency"
            />
            <BreakdownChart
                :title="$t('metaInsights.breakdown.age')"
                :rows="ageRows"
                breakdown-key="age"
                :metric="metric"
                chart-type="bar"
                :loading="loadingDemographics"
                :currency="currency"
            />
            <BreakdownChart
                :title="$t('metaInsights.breakdown.country')"
                :rows="countryRows"
                breakdown-key="country"
                :metric="metric"
                chart-type="bar"
                :loading="loadingCountry"
                :currency="currency"
            />
            <BreakdownChart
                :title="$t('metaInsights.breakdown.region')"
                :rows="regionRows"
                breakdown-key="region"
                :metric="metric"
                chart-type="bar"
                :loading="loadingRegion"
                :currency="currency"
            />
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import DateRangePicker from "./DateRangePicker.vue";
import BreakdownChart from "./BreakdownChart.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { INSIGHTS_API } from "../../../../../api/meta";

export default {
    name: "Audience",
    components: {
        DateRangePicker,
        BreakdownChart,
        SupsisButton,
    },
    props: {
        channel: { type: Object, required: true },
    },
    data() {
        return {
            dateRange: { datePreset: "last_30d" },
            metric: "impressions",
            demographicsRows: [],
            countryRowsRaw: [],
            regionRowsRaw: [],
            loadingDemographics: false,
            loadingCountry: false,
            loadingRegion: false,
        };
    },
    computed: {
        currency() {
            return _.get(this.channel, "payload.selectedAdAccount.currency", "");
        },
        loading() {
            return this.loadingDemographics || this.loadingCountry || this.loadingRegion;
        },
        genderRows() {
            const grouped = _.groupBy(this.demographicsRows || [], "gender");
            return Object.keys(grouped).map((g) => this.aggregate(grouped[g], { gender: g }));
        },
        ageRows() {
            const grouped = _.groupBy(this.demographicsRows || [], "age");
            return Object.keys(grouped)
                .sort()
                .map((a) => this.aggregate(grouped[a], { age: a }));
        },
        countryRows() {
            return (this.countryRowsRaw || []).slice(0, 15);
        },
        regionRows() {
            return (this.regionRowsRaw || []).slice(0, 15);
        },
        genderLabels() {
            return {
                male: this.$t("metaInsights.gender.male"),
                female: this.$t("metaInsights.gender.female"),
                unknown: this.$t("metaInsights.gender.unknown"),
            };
        },
    },
    methods: {
        aggregate(rows, dimensions) {
            const sum = (f) => _.sumBy(rows, (r) => Number(r[f]) || 0);
            return Object.assign(
                {
                    impressions: sum("impressions"),
                    reach: sum("reach"),
                    clicks: sum("clicks"),
                    spend: sum("spend"),
                },
                dimensions
            );
        },
        onDateRangeChange() {
            this.loadAll();
        },
        onMetricChange() {
            // Sadece chart yeniden render olur, refetch gerekmez
        },
        loadAll() {
            this.loadDemographics();
            this.loadCountry();
            this.loadRegion();
        },
        async loadDemographics() {
            if (!this.channel) return;
            this.loadingDemographics = true;
            try {
                const res = await INSIGHTS_API.getInsights(
                    this.channel._id,
                    Object.assign({}, this.dateRange, {
                        level: "account",
                        breakdowns: ["age", "gender"],
                        fields: ["impressions", "reach", "clicks", "spend"],
                        limit: 500,
                    })
                );
                this.demographicsRows = _.get(res, "data.data") || [];
            } catch (err) {
                console.error(err);
                this.demographicsRows = [];
            } finally {
                this.loadingDemographics = false;
            }
        },
        async loadCountry() {
            if (!this.channel) return;
            this.loadingCountry = true;
            try {
                const res = await INSIGHTS_API.getInsights(
                    this.channel._id,
                    Object.assign({}, this.dateRange, {
                        level: "account",
                        breakdowns: ["country"],
                        fields: ["impressions", "reach", "clicks", "spend"],
                        limit: 500,
                    })
                );
                const rows = _.get(res, "data.data") || [];
                this.countryRowsRaw = _.orderBy(
                    rows,
                    [(r) => Number(r.impressions) || 0],
                    ["desc"]
                );
            } catch (err) {
                console.error(err);
                this.countryRowsRaw = [];
            } finally {
                this.loadingCountry = false;
            }
        },
        async loadRegion() {
            if (!this.channel) return;
            this.loadingRegion = true;
            try {
                const res = await INSIGHTS_API.getInsights(
                    this.channel._id,
                    Object.assign({}, this.dateRange, {
                        level: "account",
                        breakdowns: ["region"],
                        fields: ["impressions", "reach", "clicks", "spend"],
                        limit: 500,
                    })
                );
                const rows = _.get(res, "data.data") || [];
                this.regionRowsRaw = _.orderBy(
                    rows,
                    [(r) => Number(r.impressions) || 0],
                    ["desc"]
                );
            } catch (err) {
                console.error(err);
                this.regionRowsRaw = [];
            } finally {
                this.loadingRegion = false;
            }
        },
    },
    mounted() {
        this.loadAll();
    },
};
</script>

<style lang="scss" scoped>
.meta-insights-audience {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 14px;
        border-bottom: 1px solid var(--color-border-default);

        .title-block {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .title {
                font-size: 18px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .subtitle {
                font-size: 13px;
                color: var(--color-text-muted-default);
            }
        }

        .toolbar {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 16px;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .spinning {
        animation: spin 1s linear infinite;
    }
}
</style>
